import {
  InputGroup,
  Input,
  InputRightElement,
  Icon,
  useColorModeValue,
  Tabs,
  TabList,
  Tab,
  Box,
  Button,
  useDisclosure,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  List,
  ListItem,
  Text
} from '@chakra-ui/react'
import { MagnifyingGlass, MapPin, TextAa, X } from '@phosphor-icons/react'
import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { getSuggestions } from '../utils/elasticsearchApi'

export default function SearchBar({ initialValue = '' }) {
  const [searchValue, setSearchValue] = useState(initialValue)
  const [searchType, setSearchType] = useState('location') // 'location' or 'name'
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  
  // Determine which search page we're on to know where to navigate
  const isElasticsearchPage = location.pathname === '/search-elasticsearch'
  const targetSearchPage = isElasticsearchPage ? '/search-elasticsearch' : '/search'
  
  useEffect(() => {
    if (searchValue.length > 2 && searchType === 'name') {
      const fetchSuggestions = async () => {
        try {
          const elasticsearchSuggestions = await getSuggestions(searchValue, 'name')
          if (elasticsearchSuggestions && elasticsearchSuggestions.length > 0) {
            // Transform suggestions to match expected format
            const transformedSuggestions = elasticsearchSuggestions.slice(0, 5).map((suggestion, index) => ({
              id: `suggestion-${index}`,
              name: suggestion.text || suggestion,
              location: { address: '' } // Empty location for suggestions
            }))
            setSuggestions(transformedSuggestions)
            setShowSuggestions(transformedSuggestions.length > 0)
          } else {
            setSuggestions([])
            setShowSuggestions(false)
          }
        } catch (error) {
          console.error('Error fetching suggestions:', error)
          setSuggestions([])
          setShowSuggestions(false)
        }
      }
      fetchSuggestions()
    } else {
      setSuggestions([])
      setShowSuggestions(false)
    }
  }, [searchValue, searchType])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (searchValue.trim()) {
      const currentParams = Object.fromEntries(searchParams.entries())
      navigate({
        pathname: targetSearchPage, // Use the detected search page
        search: new URLSearchParams({
          ...currentParams,
          [searchType]: searchValue.trim()
        }).toString()
      })
    }
  }

  const handleSuggestionClick = (siteName) => {
    setSearchValue(siteName)
    setShowSuggestions(false)
  }

  const clearSearch = () => {
    setSearchValue('')
    setShowSuggestions(false)
  }

  return (
    <Box position="relative" className="map-texture" p={4} borderRadius="md">
      <form onSubmit={handleSubmit}>
        <Tabs 
          variant="unstyled"
          onChange={(index) => {
            setSearchType(index === 0 ? 'location' : 'name')
            setSearchValue('')
          }}
          defaultIndex={0}
          mb={4}
        >
          <TabList 
            borderBottom="2px solid"
            borderColor="ancient.mapBorder"
            pb={1}
          >
            <Tab 
              _selected={{ 
                color: 'ancient.mapText',
                borderBottom: '2px solid',
                borderColor: 'ancient.mapHighlight',
                mb: '-2px',
                fontWeight: '600'
              }}
              color="ancient.mapText"
              fontWeight="500"
              fontSize={{ base: 'sm', md: 'md' }}
            >
              <Icon as={MapPin} mr={2} /> 
              Location
            </Tab>
            <Tab 
              _selected={{ 
                color: 'ancient.mapText',
                borderBottom: '2px solid',
                borderColor: 'ancient.mapHighlight',
                mb: '-2px',
                fontWeight: '600'
              }}
              color="ancient.mapText"
              fontWeight="500"
              fontSize={{ base: 'sm', md: 'md' }}
            >
              <Icon as={TextAa} mr={2} />
              Name
            </Tab>
          </TabList>
        </Tabs>
        
        <InputGroup size={{ base: 'md', md: 'lg' }}>
          <Input
            variant="map"
            placeholder={searchType === 'location' ? 
              'Search by location (city, state, country)...' : 
              'Search megalithic site names...'}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={() => searchType === 'name' && setShowSuggestions(true)}
            fontSize={{ base: 'sm', md: 'md' }}
          />
          {searchValue && (
            <InputRightElement width="4.5rem" mr={1}>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={clearSearch}
                color="ancient.stone"
              >
                <Icon as={X} />
              </Button>
            </InputRightElement>
          )}
          <InputRightElement>
            <Icon 
              as={MagnifyingGlass} 
              color="ancient.terracotta"
              cursor="pointer"
              onClick={handleSubmit}
              boxSize={5}
            />
          </InputRightElement>
        </InputGroup>
      </form>

      {showSuggestions && suggestions.length > 0 && (
        <Box
          position="absolute"
          width="100%"
          mt={2}
          bg="ancient.papyrus"
          border="1px solid"
          borderColor="ancient.mapBorder"
          borderRadius="md"
          shadow="md" // Use theme shadow token
          zIndex="dropdown"
          maxH="300px"
          overflowY="auto"
          className="map-texture"
        >
          <List spacing={0}>
            {suggestions.map(site => (
              <ListItem 
                key={site.id}
                p={3}
                borderBottom="1px solid"
                borderColor="ancient.mapBorder"
                _hover={{
                  bg: 'ancient.lightStone',
                  cursor: 'pointer'
                }}
                onClick={() => handleSuggestionClick(site.name)}
              >
                <Text fontWeight="600" color="ancient.mapText">{site.name}</Text>
                <Text fontSize="sm" color="ancient.inkSubtle" mt={1}>
                  Site suggestion from Elasticsearch
                </Text>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  )
}
